import { AiRegion, AiResultThresholds, Series } from "../../api/StudyApi";
import { AI_REGION_COLOR } from "./Viewport";
import { AiResult, EvalWorkflow } from "../../api/EvalWorkflowApi";
import LocalStorageService from "../../service/LocalStorageService";

export interface AiDisplayPreferences {
  displayStudyResult: boolean;

  displayResult(result: AiResult): string;

  applyRegionStyle(context: CanvasRenderingContext2D, region: AiRegion): void;

  applyAiRegionUpperTextStyle(context: CanvasRenderingContext2D): void;

  applyAiRegionLowerTextStyle(context: CanvasRenderingContext2D, obsType: string): void;

  computeAiRegionUpperText(region: AiRegion): string;

  computeAiRegionLowerText(obsType: string): string;

  computeStudyResult(study: { aiResult?: AiResult; series: Array<Series> }): AiResult;

  displayImageResults: boolean;
}

export function noStudyResult(): AiDisplayPreferences {
  return new DefaultAiDisplayPreferences(false);
}

export function demo(): AiDisplayPreferences {
  return new DefaultAiDisplayPreferences(
    true,
    false,
    false,
    false,
    (context: CanvasRenderingContext2D, region: AiRegion) => {
      context.setLineDash([10, 10]);
    },
    {
      doubt: 50,
      fract: 90,
    }
  );
}

export function aiDisplayPrefViewer(evalWorkflow: EvalWorkflow): AiDisplayPreferences {
  return new DefaultAiDisplayPreferences(evalWorkflow === EvalWorkflow.RD_AI, false, false, false);
}

export function aiDisplayPrefGmsk(displayImageResults = false): AiDisplayPreferences {
  return new DefaultAiDisplayPreferences(false, false, false, displayImageResults);
}

class DefaultAiDisplayPreferences implements AiDisplayPreferences {
  constructor(
    public displayStudyResult: boolean,
    public displayRegionConfidence = true,
    public displayAiRegionObsType = true,
    public displayImageResults = false,
    public doubtFractRegionStyle = (context: CanvasRenderingContext2D, region: AiRegion) => {},
    private thresholds?: AiResultThresholds
  ) {}

  displayResult(result: AiResult): string {
    if (result) {
      return `Study result: ${result}`;
    }
  }

  applyRegionStyle(context: CanvasRenderingContext2D, region: AiRegion): void {
    context.lineWidth = 1;
    context.strokeStyle = "white";
    context.globalAlpha = 0.5 + region.confidence / 200;
    if (this.compute(region.confidence) === AiResult.DOUBT_FRACT) {
      this.doubtFractRegionStyle(context, region);
    }
    if (region._selected) {
      context.lineWidth = context.lineWidth * 2;
    }
  }

  applyAiRegionUpperTextStyle(context: CanvasRenderingContext2D): void {
    context.globalAlpha = 0.95;
    context.fillStyle = AI_REGION_COLOR;
    context.textAlign = "left";
    context.font = "13px Arial";
  }

  applyAiRegionLowerTextStyle(context: CanvasRenderingContext2D, obsType: string): void {
    context.globalAlpha = 0.95;

    const ot = LocalStorageService.getObservationType(obsType);

    if (ot && ot.color) {
      context.fillStyle = ot.color;
    } else {
      context.fillStyle = AI_REGION_COLOR;
    }

    context.textAlign = "left";
    context.font = "13px Arial";
  }

  computeAiRegionUpperText(region: AiRegion): string {
    if (!this.displayRegionConfidence) {
      return "";
    }
    if (this.thresholds) {
      const result = this.compute(region.confidence);
      return result + " (" + region.confidence + "%)";
    }
    return "Conf. " + region.confidence + "%";
  }

  computeAiRegionLowerText(obsType: string): string {
    if (!this.displayAiRegionObsType) {
      return "";
    } else {
      return obsType.substr(0, 3);
    }
  }

  computeStudyResult(study: { aiResult?: AiResult; series: Array<Series> }): AiResult {
    if (study.aiResult) {
      return study.aiResult;
    }
    if (!this.thresholds) {
      return;
    }
    const maxFunc = (p: number, c: number) => Math.max(p, c);
    const maxConfidence: number = study.series
      .map((s) =>
        s.images
          .filter((i) => i.aiPasses)
          .map((i) => i.aiPasses)
          .reduce((acc, aiPasses) => acc.concat(aiPasses), [])
          .filter((aiPass) => aiPass.type === "FRACT")
          .map((aiPass) => aiPass.aiRegions.map((r) => r.confidence).reduce(maxFunc, -1))
          .reduce(maxFunc, -1)
      )
      .reduce(maxFunc, -1);

    return this.compute(maxConfidence);
  }

  private compute(confidence: number): AiResult {
    if (!this.thresholds || !confidence) {
      return;
    }
    if (confidence >= this.thresholds.fract) {
      return AiResult.FRACT;
    } else if (confidence >= this.thresholds.doubt) {
      return AiResult.DOUBT_FRACT;
    } else {
      return AiResult.NO_FRACT;
    }
  }
}
